import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import {
    User,
    Mail,
    Phone,
    CreditCard,
    Building2,
    MapPin,
    X,
    Save,
    AlertTriangle,
    CheckCircle,
    Eye,
    EyeOff
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { getSelectedCompanyType } from '../../../../store/selectors/AuthSelectors';
import { normalizeSSN, formatSSNForDisplay, getSSNValidationError } from '../../../../utils/ssnUtils';
import './employee-form.css';
import BrandSelect from '../../Common/BrandSelect';
import InfoPopover from '../../Common/InfoPopover';
// Bank account utilities removed - not used in employee edit form
// import { validateClearingNumber, validateAccountNumber, getBankInfo } from '../../../../utils/bankAccountUtils';

// Helper functions for field mapping
const mapEmployeeFromApi = (apiEmployee) => {
    if (!apiEmployee) return null;

    console.log('EmployeeEditModal: Raw API employee data:', apiEmployee);

    // Handle name splitting for EmployeeList data format
    let firstname = apiEmployee.firstname || '';
    let lastname = apiEmployee.lastname || '';

    // If firstname/lastname are missing but name exists (from EmployeeList API)
    if ((!firstname || !lastname) && apiEmployee.name) {
        const nameParts = apiEmployee.name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
            firstname = nameParts[0];
            lastname = nameParts.slice(1).join(' '); // Handle multiple last names
        } else if (nameParts.length === 1) {
            firstname = nameParts[0];
            lastname = ''; // Will need to be filled by user
        }
        console.log('EmployeeEditModal: Split name into:', { firstname, lastname, originalName: apiEmployee.name });
    }

    const mappedEmployee = {
        ...apiEmployee,
        // Handle name fields
        firstname,
        lastname,
        // Map backend field names to frontend field names
        // Backend uses: postnumber, city
        // Frontend uses: postnummer, ort
        // Note: Bank account fields (clearingnumber, accountnumber) removed
        postnummer: apiEmployee.postnumber || apiEmployee.postnummer || '',
        ort: apiEmployee.city || apiEmployee.ort || '',
        // Ensure optional fields have default values
        jobtitle: apiEmployee.jobtitle || '',
        phonenumber: apiEmployee.phonenumber || '',
        address: apiEmployee.address || '',
        email: apiEmployee.email || '',
        ssn: apiEmployee.ssn || '',
        employee_role: apiEmployee.employee_role || 'regular',
        // Status handling - only default to true if we have actual employee data
        active: Object.keys(apiEmployee).length > 1 ?
            (apiEmployee.status === 'active' || apiEmployee.active === true) :
            false
    };

    console.log('EmployeeEditModal: Mapped employee data:', mappedEmployee);
    return mappedEmployee;
};

const mapEmployeeToApi = (formData) => {
    return {
        ...formData,
        // Map frontend field names to backend field names
        // Note: Bank account fields (clearingnbr, accountnbr) are not sent anymore
        // as they are managed separately and not part of the employee edit form
        postnumber: formData.postnummer ? formData.postnummer.replace(/\s/g, '') : '', // Normalize postal code (remove spaces)
        city: formData.ort,
        // Normalize SSN for backend (10 digits without dash)
        ssn: normalizeSSN(formData.ssn),
        // Map active boolean to status enum
        status: formData.active ? 'active' : 'inactive',
        // Remove frontend-specific fields
        clearingnumber: undefined,
        accountnumber: undefined,
        postnummer: undefined,
        ort: undefined,
        active: undefined
    };
};

// InputField component extracted to prevent re-creation on every render
const InputField = React.memo(({
    icon: Icon,
    label,
    name,
    type = 'text',
    required = false,
    placeholder = '',
    description = null,
    showToggle = false,
    value,
    onChange,
    error,
    showValidation,
    showSSN,
    onToggleSSN
}) => (
    <div className="input-group">
        <label className="input-label">
            {label}
            {required && <span className="required-indicator">*</span>}
        </label>
        {description && (
            <p className="input-description">{description}</p>
        )}
        <div className="input-container">
            <div className="input-icon">
                <Icon size={16} />
            </div>
            <Form.Control
                type={showToggle && name === 'ssn' ? (showSSN ? 'text' : 'password') : type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`modern-input ${error ? 'error' : ''}`}
                required={required}
            />
            {showToggle && name === 'ssn' && (
                <button
                    type="button"
                    className="input-toggle"
                    onClick={onToggleSSN}
                >
                    {showSSN ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
            )}
        </div>
        {showValidation && error && (
            <div className="error-message">
                <AlertTriangle size={14} />
                <span>{error}</span>
            </div>
        )}
    </div>
));

InputField.displayName = 'InputField';

const EmployeeEditModal = ({ show, handleClose, employee, companyId, onSave }) => {
    const companyType = useSelector(getSelectedCompanyType);
    // Mirror of NewEmployee.js — surface Anställningsroll selector for every
    // juridisk person that can pay styrelsearvode / VD-lön (BAS class 72
    // routing). Backend `_buildSalaryDebits` is universally composite.
    const ROLE_SELECTOR_TYPES = new Set([
        'Aktiebolag',
        'Ekonomisk förening',
        'Ideell förening',
        'Stiftelse',
    ]);
    const showRoleSelector = ROLE_SELECTOR_TYPES.has(companyType);

    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        ssn: '',
        email: '',
        phonenumber: '',
        jobtitle: '',
        // Bank account fields removed - managed separately
        // clearingnumber: '',
        // accountnumber: '',
        address: '',
        postnummer: '',
        ort: '',
        active: true,
        employee_role: 'regular'
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [showSSN, setShowSSN] = useState(true);
    // identifiedBank state removed - bank account fields not used in this form

    // Initialize form data when modal is shown or employee changes
    useEffect(() => {
        const fetchFullEmployeeData = async () => {
            if (show && employee && employee.employee_id) {
                try {
                    console.log('EmployeeEditModal: Fetching full employee data for:', employee.employee_id);

                    // Fetch full employee details from API
                    const response = await axios.get(
                        getApiUrl(`${companyId}/hr/employees/${employee.employee_id}`),
                        { withCredentials: true }
                    );

                    const fullEmployee = response.data.data || response.data;
                    console.log('EmployeeEditModal: Full employee data:', fullEmployee);

                    const mappedEmployee = mapEmployeeFromApi(fullEmployee);
                    console.log('EmployeeEditModal: Mapped employee data:', mappedEmployee);

                    setFormData({
                        firstname: mappedEmployee.firstname || '',
                        lastname: mappedEmployee.lastname || '',
                        ssn: mappedEmployee.ssn || '',
                        email: mappedEmployee.email || '',
                        phonenumber: mappedEmployee.phonenumber || '',
                        jobtitle: mappedEmployee.jobtitle || '',
                        // Bank account fields removed
                        address: mappedEmployee.address || '',
                        postnummer: mappedEmployee.postnummer || '',
                        ort: mappedEmployee.ort || '',
                        active: mappedEmployee.active !== undefined ? mappedEmployee.active : true,
                        employee_role: mappedEmployee.employee_role || 'regular'
                    });
                    setErrors({});
                    setShowValidation(false);
                } catch (error) {
                    console.error('Error fetching full employee data:', error);
                    // Fallback to basic employee data if fetch fails
                    const mappedEmployee = mapEmployeeFromApi(employee);
                    setFormData({
                        firstname: mappedEmployee.firstname || '',
                        lastname: mappedEmployee.lastname || '',
                        ssn: mappedEmployee.ssn || '',
                        email: mappedEmployee.email || '',
                        phonenumber: mappedEmployee.phonenumber || '',
                        jobtitle: mappedEmployee.jobtitle || '',
                        // Bank account fields removed
                        address: mappedEmployee.address || '',
                        postnummer: mappedEmployee.postnummer || '',
                        ort: mappedEmployee.ort || '',
                        active: mappedEmployee.active !== undefined ? mappedEmployee.active : true,
                        employee_role: mappedEmployee.employee_role || 'regular'
                    });
                    setErrors({});
                    setShowValidation(false);
                }
            }
        };

        fetchFullEmployeeData();
    }, [show, employee, companyId]);

    // Validation rules
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.firstname.trim()) {
            newErrors.firstname = 'Förnamn är obligatoriskt';
        }
        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Efternamn är obligatoriskt';
        }
        if (!formData.ssn.trim()) {
            newErrors.ssn = 'Personnummer är obligatoriskt';
        }

        // Email validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Ogiltig e-postadress';
        }

        // Phone validation (simple Swedish format)
        if (formData.phonenumber && !/^[\d\s\-\+\(\)]{8,}$/.test(formData.phonenumber)) {
            newErrors.phonenumber = 'Ogiltigt telefonnummer';
        }

        // SSN validation using utility function
        const ssnError = getSSNValidationError(formData.ssn);
        if (ssnError) {
            newErrors.ssn = ssnError;
        }

        // Postal code validation - accept both XXXXX and XXX XX formats
        if (formData.postnummer) {
            const cleaned = formData.postnummer.replace(/\s/g, '');
            if (!/^\d{5}$/.test(cleaned)) {
                newErrors.postnummer = 'Postnummer måste vara i format XXXXX eller XXX XX';
            }
        }

        // Bank account validation removed - managed separately
        // (clearing number and account number validation)

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Bank identification removed - bank account fields not used in this form
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowValidation(true);

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const updateEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees/${employee.employee_id}`);

            // Transform form data to match backend field expectations
            const apiPayload = mapEmployeeToApi(formData);
            console.log('EmployeeEditModal: Sending API payload:', apiPayload);

            const response = await axios.put(updateEmployeeApiUrl, apiPayload, { withCredentials: true });
            console.log('EmployeeEditModal: Update response:', response.data);

            if (onSave) {
                onSave(formData);
            }
            handleClose();
        } catch (error) {
            console.error('Error updating employee:', error);
            const errorMessage = error.response?.data?.message || 'Det gick inte att uppdatera anställd. Försök igen.';
            setErrors({
                submit: errorMessage
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            firstname: '',
            lastname: '',
            ssn: '',
            email: '',
            phonenumber: '',
            jobtitle: '',
            // Bank account fields removed
            address: '',
            postnummer: '',
            ort: '',
            active: true,
            employee_role: 'regular'
        });
        setErrors({});
        setShowValidation(false);
        handleClose();
    };

    // Memoized callback for toggling SSN visibility
    const handleToggleSSN = useCallback(() => {
        setShowSSN(prev => !prev);
    }, []);

    return (
        <>

            <Modal className="emp-modal" show={show} onHide={handleCancel} size="lg" backdrop="static" centered>
                <div className="employee-modal-content">
                    {/* Header */}
                    <div className="employee-modal-header">
                        <button className="close-button" onClick={handleCancel}>
                            <X size={16} />
                        </button>
                        <div className="d-flex align-items-center gap-3">
                            <User size={24} color="#64748b" />
                            <div>
                                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    Redigera anställd
                                </h3>
                                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                    Uppdatera information för {employee?.firstname} {employee?.lastname}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="employee-modal-body">
                        <Form onSubmit={handleSubmit}>
                            {/* Info message */}
                            <div className="form-info">
                                <CheckCircle size={16} className="info-icon" />
                                <p className="info-text">
                                    Obligatoriska fält är markerade med *. All information krypteras och hanteras säkert.
                                </p>
                            </div>

                            {/* Validation Summary */}
                            {showValidation && Object.keys(errors).length > 0 && (
                                <div className="validation-summary">
                                    <AlertTriangle size={16} className="validation-icon" />
                                    <div className="validation-content">
                                        <div className="validation-title">
                                            Åtgärda följande fel innan du fortsätter:
                                        </div>
                                        <ul className="validation-list">
                                            {Object.values(errors).filter(error => error).map((error, index) => (
                                                <li key={index} className="validation-item">• {error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Personal Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <User size={18} />
                                    Personuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={User}
                                        label="Förnamn"
                                        name="firstname"
                                        required={true}
                                        placeholder="Ange förnamn"
                                        value={formData.firstname}
                                        onChange={handleInputChange}
                                        error={errors.firstname}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={User}
                                        label="Efternamn"
                                        name="lastname"
                                        required={true}
                                        placeholder="Ange efternamn"
                                        value={formData.lastname}
                                        onChange={handleInputChange}
                                        error={errors.lastname}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={CreditCard}
                                        label="Personnummer (ÅÅMMDD-XXXX)"
                                        name="ssn"
                                        required={true}
                                        placeholder="ÅÅMMDD-XXXX"
                                        showToggle={true}
                                        value={formData.ssn}
                                        onChange={handleInputChange}
                                        error={errors.ssn}
                                        showValidation={showValidation}
                                        showSSN={showSSN}
                                        onToggleSSN={handleToggleSSN}
                                    />

                                    <InputField
                                        icon={Building2}
                                        label="Jobbtitel"
                                        name="jobtitle"
                                        placeholder="Ange jobbtitel"
                                        value={formData.jobtitle}
                                        onChange={handleInputChange}
                                        error={errors.jobtitle}
                                        showValidation={showValidation}
                                    />

                                    {showRoleSelector && (
                                        <div className="input-group" data-testid="employee-role-selector">
                                            <label className="input-label">
                                                Anställningsroll
                                                <InfoPopover title="Anställningsroll" ariaLabel="Om anställningsroll">
                                                    Styr vilket lönekonto som debiteras i lönekörningens verifikat: 7210 för vanlig anställd, 7220 för företagsledare (ABL 8 kap.) och 7240 för styrelsearvode.
                                                </InfoPopover>
                                            </label>
                                            <div className="input-container">
                                                <BrandSelect
                                                    name="employee_role"
                                                    value={formData.employee_role}
                                                    onChange={handleInputChange}
                                                    options={[
                                                        { value: 'regular', label: 'Vanlig anställd (BAS 7210)' },
                                                        { value: 'company_director', label: 'Företagsledare (BAS 7220)' },
                                                        { value: 'board_member', label: 'Styrelseledamot — arvode (BAS 7240)' },
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <Mail size={18} />
                                    Kontaktuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={Mail}
                                        label="E-postadress"
                                        name="email"
                                        type="email"
                                        placeholder="namn@exempel.se"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={errors.email}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={Phone}
                                        label="Telefonnummer"
                                        name="phonenumber"
                                        placeholder="+46 XX XXX XX XX"
                                        value={formData.phonenumber}
                                        onChange={handleInputChange}
                                        error={errors.phonenumber}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="form-section">
                                <div className="section-title">
                                    <MapPin size={18} />
                                    Adressuppgifter
                                </div>

                                <div className="form-grid">
                                    <InputField
                                        icon={MapPin}
                                        label="Adress"
                                        name="address"
                                        placeholder="Gatunamn XX"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        error={errors.address}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={MapPin}
                                        label="Postnummer"
                                        name="postnummer"
                                        placeholder="XXX XX"
                                        value={formData.postnummer}
                                        onChange={handleInputChange}
                                        error={errors.postnummer}
                                        showValidation={showValidation}
                                    />

                                    <InputField
                                        icon={MapPin}
                                        label="Ort"
                                        name="ort"
                                        placeholder="Ortnamn"
                                        value={formData.ort}
                                        onChange={handleInputChange}
                                        error={errors.ort}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>

                            {/* Bank Information 
                            <div className="form-section">
                                <div className="section-title">
                                    <CreditCard size={18} />
                                    Bankuppgifter
                                </div>

                                <div className="form-grid">
                                    <div>
                                        <InputField
                                            icon={CreditCard}
                                            label="Clearingnummer"
                                            name="clearingnumber"
                                            placeholder="XXXX"
                                            description="4-5 siffror"
                                            value={formData.clearingnumber}
                                            onChange={handleInputChange}
                                            error={errors.clearingnumber}
                                            showValidation={showValidation}
                                        />
                                        {identifiedBank && !errors.clearingnumber && formData.clearingnumber && (
                                            <div style={{
                                                marginTop: '4px',
                                                fontSize: '0.75rem',
                                                color: '#10b981',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                paddingLeft: '36px'
                                            }}>
                                                <CheckCircle size={12} />
                                                <span>{identifiedBank}</span>
                                            </div>
                                        )}
                                    </div>

                                    <InputField
                                        icon={CreditCard}
                                        label="Kontonummer"
                                        name="accountnumber"
                                        placeholder="XXXXXXXXX"
                                        description="Kontonummer utan bindestreck"
                                        value={formData.accountnumber}
                                        onChange={handleInputChange}
                                        error={errors.accountnumber}
                                        showValidation={showValidation}
                                    />
                                </div>
                            </div>
                            */}

                            {/* Status */}
                            <div className="checkbox-group">
                                <Form.Check
                                    type="checkbox"
                                    id="active-checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleInputChange}
                                />
                                <label className="checkbox-label" htmlFor="active-checkbox">
                                    Aktiv anställd
                                </label>
                            </div>
                        </Form>
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                            * Obligatoriska fält
                        </div>
                        <div className="d-flex gap-3">
                            <button
                                className="action-button btn-cancel"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Avbryt
                            </button>
                            <button
                                className="action-button btn-save"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        Sparar...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Spara ändringar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default EmployeeEditModal;